import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Users, X, Loader2, UserPlus, UserMinus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { BulkReassignActions, BulkRemoveConfirmation } from './BulkReassignControls';
import { AssignedUserRemoval } from './AssignedUserRemoval';
import { AssignmentFunctionPicker } from './AssignmentFunctionPicker';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABELS = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  gestor: 'Gestor',
  mediador: 'Mediador',
  inversor: 'Inversor',
};

const ROLE_BADGE = {
  superadmin: 'bg-red-100 text-red-700',
  admin: 'bg-purple-100 text-purple-700',
  gestor: 'bg-blue-100 text-blue-700',
  mediador: 'bg-amber-100 text-amber-700',
  inversor: 'bg-emerald-100 text-emerald-700',
};

/**
 * Modal to bulk-assign a list of pre-selected assets to a single user.
 * Used from AssetsPage → "Asignar a" button.
 *
 * Props:
 *   open       (bool)
 *   onClose    (fn)
 *   assetIds   (str[])  Pre-selected asset ids from AssetsPage.
 *   onDone     (fn)     Called after a successful assignment.
 */
const BulkAssignModal = ({ open, onClose, assetIds = [], onDone, initialNote = '', allowClear = false }) => {
  const [action, setAction] = useState('assign');
  const [confirmed, setConfirmed] = useState(false);
  const prefix = allowClear ? 'bulk-reassign' : 'bulk-assign';
  const removing = allowClear && action === 'remove';
  const [removalChoice, setRemovalChoice] = useState({ scope: null, users: [] });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pickedUserId, setPickedUserId] = useState(null);
  const [search, setSearch] = useState('');
  // Nota compartida: si el usuario ya escribió una en la barra bulk de la
  // página, la traemos precargada; si no, permite escribirla aquí.
  const [note, setNote] = useState(initialNote || '');
  // (Feb 2026) Función explícita del usuario para ESE activo. Independiente
  // de su rol global. Si no se toca, se envía null y el backend aplica el
  // fallback histórico basado en el rol.
  const [funcion, setFuncion] = useState('gestor');
  const removalScope = JSON.stringify([funcion, assetIds]);
  const pickedRemovals = removalChoice.scope === removalScope ? removalChoice.users : [];
  const canRemove = confirmed && pickedRemovals.length > 0;
  const changeFunction = value => { setFuncion(value); setConfirmed(false); setRemovalChoice({ scope: null, users: [] }); };
  useEffect(() => { setRemovalChoice({ scope: null, users: [] }); setConfirmed(false); }, [removalScope, open]);

  useEffect(() => {
    if (!open) return;
    setPickedUserId(null);
    setSearch('');
    setNote(initialNote || '');
    setFuncion('gestor');
    setAction('assign');
    setConfirmed(false);
    setUsers([]);
    setLoading(true);
    axios.get(`${API}/users`, { withCredentials: true })
      .then(r => {
        // Endpoint returns either an array or {items: []}
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        // Only assignable roles (admins see all already so it would be a no-op
        // but we still allow it; superadmin is skipped to avoid noise).
        setUsers(list);
      })
      .catch(() => toast.error('Error al cargar usuarios'))
      .finally(() => setLoading(false));
  }, [open, initialNote]);

  // Cuando el operador elige un usuario, si su rol coincide con una función
  // asignable, pre-seleccionamos ese rol como default de "Función" para
  // reducir clicks. El operador siempre puede cambiarlo.
  useEffect(() => {
    if (!pickedUserId) return;
    const u = users.find(x => x.id === pickedUserId);
    const r = (u?.role || '').toLowerCase();
    if (['gestor', 'mediador', 'inversor'].includes(r)) {
      setFuncion(r);
    }
  }, [pickedUserId, users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const submit = async () => {
    if (submitting || assetIds.length === 0 || (removing ? !canRemove : !pickedUserId)) return;
    setSubmitting(true);
    try {
      const payload = { asset_ids: assetIds, funcion, action: removing ? 'remove' : 'assign' };
      if (removing) {
        payload.confirm_remove = true;
        payload.user_ids = [...new Set(pickedRemovals.flatMap(user => user.assignment_ids))];
      }
      else payload.user_id = pickedUserId;
      const trimmedNote = (note || '').trim();
      if (trimmedNote) payload.nota = trimmedNote;
      const r = await axios.post(
        `${API}/admin/assets/bulk-assign`,
        payload,
        { withCredentials: true },
      );
      const d = r.data || {};
      const picked = users.find(u => u.id === pickedUserId);
      const target = picked?.name || picked?.email || 'usuario';
      const funcionLabel = { gestor: 'Gestor', mediador: 'Mediador', inversor: 'Inversor' }[funcion] || funcion;
      const parts = [];
      if (d.modified_assets) parts.push(`Personas marcadas retiradas de ${funcionLabel} en ${d.modified_assets} activo${d.modified_assets === 1 ? '' : 's'}`);
      if (d.unchanged_assets) parts.push(`${d.unchanged_assets} sin coincidencias`);
      if (d.assigned) parts.push(`${d.assigned} asignado${d.assigned === 1 ? '' : 's'} a ${target} como ${funcionLabel}`);
      if (d.already_assigned) parts.push(`${d.already_assigned} ya tenía`);
      if (d.not_found) parts.push(`${d.not_found} no encontrado${d.not_found === 1 ? '' : 's'}`);
      if (d.notes_added) parts.push('nota añadida');
      const resultMessage = <span data-testid={`${prefix}-result`}>{parts.join(' · ') || 'Sin cambios'}</span>;
      if (d.not_found) toast.warning(resultMessage, { id: `${prefix}-result` });
      else toast.success(resultMessage, { id: `${prefix}-result` });
      if (d.note_error) toast.warning(<span data-testid={`${prefix}-note-error`}>{d.note_error}</span>, { id: `${prefix}-note-error` });
      onDone && onDone(d);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(<span data-testid={`${prefix}-error`}>{typeof detail === 'string' ? detail : 'No se pudo guardar la asignación'}</span>, { id: `${prefix}-error` });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const close = () => { if (!submitting) onClose(); };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
      onClick={close}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}
      data-testid={`${prefix}-modal`}
      role="dialog" aria-modal="true" aria-labelledby={`${prefix}-title`}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90dvh] sm:max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 id={`${prefix}-title`} data-testid={`${prefix}-title`} className="font-semibold text-slate-800">{allowClear ? 'Reasignar activos' : 'Asignar a usuario'}</h3>
              <p data-testid={`${prefix}-selection-count`} className="text-xs text-slate-500 mt-0.5">{assetIds.length} activo{assetIds.length === 1 ? '' : 's'} seleccionado{assetIds.length === 1 ? '' : 's'}</p>
            </div>
          </div>
          <button onClick={close} disabled={submitting} aria-label="Cerrar" className="p-1 hover:bg-slate-100 rounded" data-testid={`${prefix}-close`}>
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {allowClear && <>
          <BulkReassignActions action={action} onChange={value => { setAction(value); setConfirmed(false); setRemovalChoice({ scope: null, users: [] }); }} disabled={submitting} />
          <div className="px-4 py-3 border-b border-slate-100 shrink-0">
            <AssignmentFunctionPicker prefix={prefix} value={funcion} onChange={changeFunction} disabled={submitting} />
          </div>
        </>}

        {!removing && <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              disabled={submitting}
              aria-label="Buscar usuarios"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email o rol"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:border-blue-500"
              autoFocus
              data-testid={`${prefix}-search`}
            />
          </div>
        </div>}

        <div className="overflow-y-auto flex-1 min-h-0">
          {removing ? <AssignedUserRemoval key={removalScope} assetIds={assetIds} funcion={funcion}
            selected={pickedRemovals} onChange={selected => { setRemovalChoice({ scope: removalScope, users: selected }); setConfirmed(false); }} disabled={submitting} /> : loading ? (
            <div data-testid={`${prefix}-loading`} className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <p className="text-sm">Cargando usuarios…</p>
            </div>
          ) : filtered.length === 0 ? (
            <p data-testid={`${prefix}-empty-users`} className="p-8 text-center text-sm text-slate-400">Sin usuarios coincidentes</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {filtered.map(u => {
                const sel = pickedUserId === u.id;
                const role = (u.role || '').toLowerCase();
                const isAssignable = ['gestor', 'mediador', 'inversor'].includes(role);
                return (
                  <button
                    key={u.id}
                    onClick={() => setPickedUserId(u.id)}
                    disabled={submitting}
                    aria-pressed={sel}
                    className={`w-full text-left px-4 py-3 hover:bg-blue-50/40 ${sel ? 'bg-blue-50' : ''}`}
                    data-testid={`${prefix}-user-${u.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p data-testid={`${prefix}-user-name-${u.id}`} className="font-medium text-sm text-slate-800 truncate">{u.name || u.email}</p>
                        <p data-testid={`${prefix}-user-email-${u.id}`} className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span data-testid={`${prefix}-user-role-${u.id}`} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ROLE_BADGE[role] || 'bg-slate-100 text-slate-600'}`}>
                          {ROLE_LABELS[role] || u.role}
                        </span>
                        {!isAssignable && (
                          <span data-testid={`${prefix}-user-access-${u.id}`} className="text-[10px] text-slate-400 italic">acceso global</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 space-y-3 shrink-0">
          {!allowClear && <AssignmentFunctionPicker prefix={prefix} value={funcion} onChange={changeFunction} disabled={submitting} showHint />}
          {removing && <BulkRemoveConfirmation label={ROLE_LABELS[funcion]} userCount={pickedRemovals.length}
            assetCount={new Set(pickedRemovals.flatMap(user => user.asset_ids)).size}
            confirmed={canRemove} onChange={setConfirmed} disabled={submitting} />}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor={`${prefix}-note`}>
              Nota compartida (opcional) — se añadirá a la pestaña Notas de cada activo{removing ? ' modificado' : ''}
            </label>
            <textarea
              id={`${prefix}-note`}
              disabled={submitting}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="p. ej. Ronda comercial junio 2026 — llamar tras 48h"
              rows={2}
              maxLength={2000}
              className="input-base resize-none"
              data-testid={`${prefix}-note-input`}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {!removing && <span data-testid={`${prefix}-user-count`} className="text-xs text-slate-500 flex items-center gap-1">
              <Users className="w-3 h-3" />
              {filtered.length} usuario{filtered.length === 1 ? '' : 's'}
            </span>}
            <div className="flex gap-2 ml-auto">
              <button onClick={close} disabled={submitting} data-testid={`${prefix}-cancel`} className="px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-sm">Cancelar</button>
              <button
                onClick={submit}
                disabled={(removing ? !canRemove : !pickedUserId) || submitting || assetIds.length === 0}
                className={`px-3 py-2 rounded-md text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1 transition-colors ${removing ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                data-testid={`${prefix}-confirm`}
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : removing ? <UserMinus className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                {submitting ? 'Guardando…' : removing ? 'Quitar personas marcadas' : allowClear ? 'Añadir usuario' : 'Asignar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;
